// src/pages/Dashboard/Admin/Coaching/components/CoachingKpiCards.jsx

import React from "react";
import {
  FaCalendarCheck,
  FaClock,
  FaExclamationTriangle,
  FaTrophy,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";

import { formatDateFR, formatHours } from "../utils/coachingDashboardUtils";

function KpiCard({ label, value, detail, icon, loading, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
    ok: "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10",
    warn: "border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10",
    danger: "border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10",
    info: "border-indigo-200 bg-indigo-50 dark:border-indigo-500/20 dark:bg-indigo-500/10",
  };

  return (
    <article
      className={`rounded-[24px] border p-4 shadow-sm ${tones[tone] || tones.base}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            {label}
          </p>

          {loading ? (
            <div className="mt-3 h-7 w-24 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
          ) : (
            <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
              {value}
            </p>
          )}

          {detail ? (
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
              {detail}
            </p>
          ) : null}
        </div>

        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-indigo-300 dark:ring-slate-700">
          {icon}
        </div>
      </div>
    </article>
  );
}

export default function CoachingKpiCards({
  stats,
  selectedTeacherStats,
  loading,
  hasSelectedTeacher,
}) {
  return (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
      <KpiCard
        label="Enseignants"
        value={stats.totalTeachers}
        detail={`${stats.teachersWithHours} avec heures enregistrées`}
        icon={<FaUsers />}
        loading={loading}
        tone="info"
      />

      <KpiCard
        label="Total heures"
        value={formatHours(stats.totalHours)}
        detail="Toutes compétitions confondues"
        icon={<FaClock />}
        loading={loading}
        tone="ok"
      />

      <KpiCard
        label="Événements"
        value={stats.totalEvents}
        detail="Inscriptions coaching"
        icon={<FaCalendarCheck />}
        loading={loading}
      />

      <KpiCard
        label="Moyenne"
        value={formatHours(stats.averageHours)}
        detail="Par enseignant"
        icon={<FaUserCheck />}
        loading={loading}
      />

      <KpiCard
        label="À compléter"
        value={stats.eventsWithoutHours}
        detail="Événements sans heure"
        icon={<FaExclamationTriangle />}
        loading={loading}
        tone={stats.eventsWithoutHours ? "warn" : "base"}
      />

      <KpiCard
        label="Top enseignant"
        value={stats.topTeacher?.teacherName || "—"}
        detail={
          stats.topTeacher
            ? formatHours(stats.topTeacher.totalHours)
            : "Aucune donnée"
        }
        icon={<FaTrophy />}
        loading={loading}
        tone="info"
      />

      {hasSelectedTeacher ? (
        <>
          <KpiCard
            label="Sélection"
            value={formatHours(selectedTeacherStats.totalHours)}
            detail={`${selectedTeacherStats.totalEvents} événement(s)`}
            icon={<FaClock />}
            loading={false}
            tone="ok"
          />

          <KpiCard
            label="Dernier événement"
            value={formatDateFR(selectedTeacherStats.lastEventDate)}
            detail={selectedTeacherStats.teacherName}
            icon={<FaCalendarCheck />}
            loading={false}
          />
        </>
      ) : null}
    </section>
  );
}